import * as Sentry from '@sentry/react';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { api } from '@lib/api/client';
import { cartKeys } from '@lib/query/options/cart';
import { checkoutKeys } from '@lib/query/options/checkout';
import { productKeys } from '@lib/query/options/products';
import type { AddLineItemInput, BatchAddLineItemsInput } from '@marxbiotech/shared-types/bff';
import type { EnhancedStoreCart } from '@marxbiotech/shared-types/medusa';

// =============================================================================
// API Response Types
// =============================================================================

interface CartMutationResponse {
  cart: EnhancedStoreCart;
}

interface DeleteLineItemResponse {
  success: boolean;
}

// =============================================================================
// Mutation Keys
// =============================================================================

/**
 * Mutation keys for cart operations.
 * Used by useIsMutating to detect in-flight mutations.
 */
export const cartMutationKeys = {
  lineItem: {
    update: ['cart', 'lineItem', 'update'] as const,
    delete: ['cart', 'lineItem', 'delete'] as const,
  },
};

// =============================================================================
// Add Line Item
// =============================================================================

type UseAddLineItemOptions = Omit<
  UseMutationOptions<EnhancedStoreCart, Error, AddLineItemInput>,
  'mutationFn'
>;

export function useAddLineItem(options?: UseAddLineItemOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddLineItemInput): Promise<EnhancedStoreCart> => {
      const response = await api.post<CartMutationResponse>(
        '/carts/current/line-items',
        data
      );
      return response.data.cart;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
      // 購物車變更影響 bundle 虛擬庫存，需刷新商品查詢
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      options?.onSuccess?.(...args);
    },
    onError: (error, variables, ...args) => {
      Sentry.captureException(error, {
        tags: { mutation: 'addLineItem' },
        extra: { variables },
      });
      options?.onError?.(error, variables, ...args);
    },
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Batch Add Line Items
// =============================================================================

type UseBatchAddLineItemsOptions = Omit<
  UseMutationOptions<EnhancedStoreCart, Error, BatchAddLineItemsInput>,
  'mutationFn'
>;

export function useBatchAddLineItems(options?: UseBatchAddLineItemsOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: BatchAddLineItemsInput
    ): Promise<EnhancedStoreCart> => {
      const response = await api.post<CartMutationResponse>(
        '/carts/current/line-items/batch',
        data
      );
      return response.data.cart;
    },
    onSuccess: options?.onSuccess,
    onError: (error, variables, ...args) => {
      Sentry.captureException(error, {
        tags: { mutation: 'batchAddLineItems' },
        extra: { variantIds: variables.items.map(i => i.variant_id), itemCount: variables.items.length },
      });
      options?.onError?.(error, variables, ...args);
    },
    onSettled: (...args) => {
      // Batch add is sequential: if item N fails, items 1..N-1 are already committed.
      // Always invalidate cart cache so UI reflects the (partial-)success state.
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      options?.onSettled?.(...args);
    },
  });
}

// =============================================================================
// Update Line Item
// =============================================================================

interface UpdateLineItemInput {
  lineItemId: string;
  quantity: number;
}

type UseUpdateLineItemOptions = Omit<
  UseMutationOptions<EnhancedStoreCart, Error, UpdateLineItemInput>,
  'mutationFn'
>;

export function useUpdateLineItem(options?: UseUpdateLineItemOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: cartMutationKeys.lineItem.update,
    mutationFn: async ({
      lineItemId,
      quantity,
    }: UpdateLineItemInput): Promise<EnhancedStoreCart> => {
      const response = await api.patch<CartMutationResponse>(
        `/carts/current/line-items/${lineItemId}`,
        { quantity }
      );
      return response.data.cart;
    },
    onSuccess: (...args) => {
      // Invalidate both cart and checkout queries to update all UI components
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
      queryClient.invalidateQueries({ queryKey: checkoutKeys.data() });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      options?.onSuccess?.(...args);
    },
    onError: (error, variables, ...args) => {
      Sentry.captureException(error, {
        tags: { mutation: 'updateLineItem' },
        extra: { variables },
      });
      options?.onError?.(error, variables, ...args);
    },
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Delete Line Item
// =============================================================================

type UseDeleteLineItemOptions = Omit<
  UseMutationOptions<void, Error, string>,
  'mutationFn'
>;

export function useDeleteLineItem(options?: UseDeleteLineItemOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: cartMutationKeys.lineItem.delete,
    mutationFn: async (lineItemId: string): Promise<void> => {
      await api.delete<DeleteLineItemResponse>(
        `/carts/current/line-items/${lineItemId}`
      );
    },
    onSuccess: (...args) => {
      // Invalidate both cart and checkout queries to update all UI components
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
      queryClient.invalidateQueries({ queryKey: checkoutKeys.data() });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      options?.onSuccess?.(...args);
    },
    onError: (error, variables, ...args) => {
      Sentry.captureException(error, {
        tags: { mutation: 'deleteLineItem' },
        extra: { lineItemId: variables },
      });
      options?.onError?.(error, variables, ...args);
    },
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Update Cart Promo Codes
// =============================================================================

interface UpdateCartPromoCodesInput {
  promo_codes: string[];
}

type UseUpdateCartPromoCodesOptions = Omit<
  UseMutationOptions<EnhancedStoreCart, Error, UpdateCartPromoCodesInput>,
  'mutationFn'
>;

export function useUpdateCartPromoCodes(options?: UseUpdateCartPromoCodesOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCartPromoCodesInput): Promise<EnhancedStoreCart> => {
      const response = await api.patch<CartMutationResponse>(
        '/carts/current',
        data
      );
      return response.data.cart;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: cartKeys.all });
      queryClient.invalidateQueries({ queryKey: checkoutKeys.all });
      options?.onSuccess?.(...args);
    },
    onError: (error, ...args) => {
      Sentry.captureException(error, {
        tags: { mutation: 'updateCartPromoCodes' },
      });
      options?.onError?.(error, ...args);
    },
    onSettled: options?.onSettled,
  });
}
